// gw API client for the web app. Mirrors the contract of ui/src/api/client.js:
// every request carries the Firebase ID token as a bearer, and a 402 means the
// subscription gate tripped (surfaced via the onPaymentRequired hook).
import { auth } from './firebase';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1';

if (/localhost|127\.0\.0\.1/.test(API_BASE_URL) && import.meta.env.PROD) {
  console.warn(
    '[api] Production build points at a localhost API base. ' +
    'Set VITE_API_BASE_URL to the gw Cloud Run URL before deploying.'
  );
}

async function authHeader() {
  const user = auth.currentUser;
  if (!user) return {};
  try {
    return { Authorization: `Bearer ${await user.getIdToken()}` };
  } catch {
    return {};
  }
}

// Raw bearer token, for callers that build their own request (e.g. hls.js's
// xhrSetup, which is synchronous and can't await authHeader).
export async function getIdToken() {
  const user = auth.currentUser;
  if (!user) return null;
  try { return await user.getIdToken(); } catch { return null; }
}

// Single consumer (the subscription layer, added in W2) registers here so any
// 402 can route to the paywall regardless of which call surfaced it.
let onPaymentRequired = null;
export function setOnPaymentRequired(cb) { onPaymentRequired = cb; }

async function request(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(await authHeader()),
    ...(options.headers || {}),
  };
  const res = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });

  if (res.status === 402) {
    if (onPaymentRequired) { try { onPaymentRequired(); } catch { /* ignore */ } }
    const e = new Error('NO_SUBSCRIPTION');
    e.code = 'NO_SUBSCRIPTION';
    e.status = 402;
    throw e;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const e = new Error(`HTTP ${res.status} ${res.statusText}: ${text}`);
    e.status = res.status;
    // Parsed JSON error body when the server sent one (e.g. {code, message}).
    try { e.data = text ? JSON.parse(text) : null; } catch { e.data = null; }
    throw e;
  }
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : null;
}

// Multipart upload. On web the picked value is a real File from
// <input type="file">, so we append it directly — the browser sets the
// multipart Content-Type + boundary (do NOT set it manually).
async function uploadFile(path, file) {
  const headers = await authHeader(); // bearer only; no Content-Type
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE_URL}${path}`, { method: 'POST', headers, body: form });
  if (res.status === 402) {
    if (onPaymentRequired) { try { onPaymentRequired(); } catch { /* ignore */ } }
    const e = new Error('NO_SUBSCRIPTION'); e.code = 'NO_SUBSCRIPTION'; e.status = 402; throw e;
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    // Prefer a server-provided JSON `message` (e.g. the friendly 413 from
    // UploadExceptionHandler) over the raw status line.
    let msg = `HTTP ${res.status} ${res.statusText}: ${text}`;
    try { const j = JSON.parse(text); if (j && (j.message || j.error)) msg = j.message || j.error; } catch { /* not JSON */ }
    const e = new Error(msg); e.status = res.status; throw e;
  }
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : null;
}

// Multipart routing threshold: files larger than this take the signed-URL
// direct-to-GCS path instead of a multipart POST through gw.
//
// In prod it mirrors gw's spring.servlet.multipart.max-file-size (30 MB),
// just under Cloud Run's 32 MiB request cap. Against a localhost gw there's
// no Cloud Run cap and no GCS to sign against, so we lift the threshold far
// up — every file goes multipart, and gw accepts it as long as
// MULTIPART_MAX_SIZE is raised there too (e.g. 1024MB).
const IS_LOCAL_API = /localhost|127\.0\.0\.1/.test(API_BASE_URL);
export const MAX_UPLOAD_BYTES = IS_LOCAL_API ? 4 * 1024 * 1024 * 1024 : 30 * 1024 * 1024;

export const api = {
  baseUrl: API_BASE_URL,

  // Identity (paths verified against ui/src/api/client.js)
  getMe: () => request('/me'),
  updateMe: (patch) => request('/me', { method: 'PUT', body: JSON.stringify(patch) }),
  deleteMe: () => request('/me', { method: 'DELETE' }),
  getPersonProfile: (personId) => request(`/persons/${personId}/profile`),

  // Events
  listEvents: () => request('/events'),
  getEvent: (id) => request(`/events/${id}`),
  createEvent: (body) => request('/events', { method: 'POST', body: JSON.stringify(body) }),
  updateEvent: (id, body) => request(`/events/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteEvent: (id) => request(`/events/${id}`, { method: 'DELETE' }),
  getEventMessages: (id) => request(`/events/${id}/messages`),
  uploadEventIcon: (id, file) => uploadFile(`/events/${id}/icon`, file),
  deleteEventIcon: (id) => request(`/events/${id}/icon`, { method: 'DELETE' }),
  listEventFiles: (id) => request(`/events/${id}/files`),
  uploadEventFile: (id, file) => uploadFile(`/events/${id}/files`, file),
  deleteEventFile: (id, fileId) => request(`/events/${id}/files/${fileId}`, { method: 'DELETE' }),

  // Groups
  listGroups: () => request('/groups'),
  browseGroups: () => request('/groups/browse'),
  browseEvents: () => request('/events/browse'),
  joinGroup: (id) => request(`/groups/${id}/join`, { method: 'POST' }),
  joinEvent: (id) => request(`/events/${id}/join`, { method: 'POST' }),
  browseLessons: () => request('/learn/lessons/browse'),
  getGroup: (id) => request(`/groups/${id}`),
  createGroup: (body) => request('/groups', { method: 'POST', body: JSON.stringify(body) }),
  updateGroup: (id, body) => request(`/groups/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteGroup: (id) => request(`/groups/${id}`, { method: 'DELETE' }),
  removeGroupMember: (groupId, personId) => request(`/groups/${groupId}/members/${personId}`, { method: 'DELETE' }),
  transferGroupAdmin: (groupId, newAdminPersonId) =>
    request(`/groups/${groupId}/transfer-admin`, { method: 'POST', body: JSON.stringify({ newAdminPersonId }) }),
  createGroupEvent: (groupId, body) =>
    request(`/groups/${groupId}/events`, { method: 'POST', body: JSON.stringify(body) }),
  uploadGroupIcon: (id, file) => uploadFile(`/groups/${id}/icon`, file),
  registerForGroup: (id) => request(`/groups/${id}/register`, { method: 'POST' }),
  deleteGroupIcon: (id) => request(`/groups/${id}/icon`, { method: 'DELETE' }),
  listGroupFiles: (id) => request(`/groups/${id}/files`),
  uploadGroupFile: (id, file) => uploadFile(`/groups/${id}/files`, file),
  deleteGroupFile: (id, fileId) => request(`/groups/${id}/files/${fileId}`, { method: 'DELETE' }),

  // Group invites (admin side)
  listGroupInvites: (groupId) => request(`/groups/${groupId}/invites`),
  inviteToGroup: (groupId, { email, name, freeAccess }) =>
    request(`/groups/${groupId}/invites`, { method: 'POST', body: JSON.stringify({ email, name, freeAccess: !!freeAccess }) }),
  resendInvite: (groupId, inviteId) => request(`/groups/${groupId}/invites/${inviteId}/resend`, { method: 'POST' }),
  revokeInvite: (groupId, inviteId) => request(`/groups/${groupId}/invites/${inviteId}`, { method: 'DELETE' }),

  // Invites (invitee side)
  listMyInvites: () => request('/invites/me'),
  getInvite: (token) => request(`/invites/${encodeURIComponent(token)}`),
  acceptInvite: (token) => request(`/invites/${encodeURIComponent(token)}/accept`, { method: 'POST' }),
  declineInvite: (token) => request(`/invites/${encodeURIComponent(token)}/decline`, { method: 'POST' }),

  // Course invitations (design/018 Phase 3) — admin side
  adminListCourseInvites: (courseId) => request(`/admin/learn/courses/${courseId}/invites`),
  adminInviteToCourse: (courseId, { email, name, freeAccess }) =>
    request(`/admin/learn/courses/${courseId}/invites`, { method: 'POST', body: JSON.stringify({ email, name, freeAccess: !!freeAccess }) }),
  adminResendCourseInvite: (courseId, inviteId) =>
    request(`/admin/learn/courses/${courseId}/invites/${inviteId}/resend`, { method: 'POST' }),
  adminRevokeCourseInvite: (courseId, inviteId) =>
    request(`/admin/learn/courses/${courseId}/invites/${inviteId}`, { method: 'DELETE' }),
  // Course invitations — invitee side (web-only). Accept by token or 6-digit code.
  listMyCourseInvites: () => request('/learn/invites/me'),
  acceptCourseInvite: (tokenOrCode) =>
    request(`/learn/invites/${encodeURIComponent(tokenOrCode)}/accept`, { method: 'POST' }),
  declineCourseInvite: (tokenOrCode) =>
    request(`/learn/invites/${encodeURIComponent(tokenOrCode)}/decline`, { method: 'POST' }),

  // Learn (design/009) — reader
  learnLibrary: () => request('/learn/library'),
  getLearnCourse: (id) => request(`/learn/courses/${id}`),
  // Enrollment (design/018) — web only.
  learnEnroll: (id) => request(`/learn/courses/${id}/enroll`, { method: 'POST' }),
  learnUnenroll: (id) => request(`/learn/courses/${id}/enroll`, { method: 'DELETE' }),
  // Paid course → returns { url } for the Stripe Checkout page (design/018 Ph2).
  learnCheckout: (id) => request(`/learn/courses/${id}/checkout`, { method: 'POST' }),
  learnMyEnrollments: () => request('/learn/my/enrollments'),
  getLearnLesson: (id) => request(`/learn/lessons/${id}`),
  getLearnProgress: () => request('/learn/progress'),
  saveLearnProgress: (lessonId, body) =>
    request(`/learn/lessons/${lessonId}/progress`, { method: 'PUT', body: JSON.stringify(body) }),
  // Fetch a media block (bearer-authed) and return a blob object URL for
  // <audio>/<video> src. Caller should URL.revokeObjectURL when done.
  blockMediaObjectUrl: async (blockId) => {
    const res = await fetch(`${API_BASE_URL}/learn/blocks/${blockId}/media`, { headers: await authHeader() });
    if (!res.ok) { const e = new Error(`HTTP ${res.status}`); e.status = res.status; throw e; }
    return URL.createObjectURL(await res.blob());
  },
  // Raw bytes of a media block, for in-browser rendering (e.g. mammoth docx→HTML).
  blockMediaArrayBuffer: async (blockId) => {
    const res = await fetch(`${API_BASE_URL}/learn/blocks/${blockId}/media`, { headers: await authHeader() });
    if (!res.ok) { const e = new Error(`HTTP ${res.status}`); e.status = res.status; throw e; }
    return res.arrayBuffer();
  },
  // Signed direct URL for a media block (browser fetches straight from GCS —
  // no buffering through gw, supports range/seeking). { url } may be null on a
  // local store, in which case fall back to blockMediaObjectUrl.
  learnBlockMediaUrl: (blockId) => request(`/learn/blocks/${blockId}/media-url`),
  // HLS manifest URL for transcoded video (B4.1b). hls.js loads this with a
  // bearer header (added in xhrSetup); segments inside are signed GCS URLs.
  learnHlsUrl: (blockId) => `${API_BASE_URL}/learn/blocks/${blockId}/hls`,
  // Ask AI about a lesson (B4.4) → { answer, citations:[{filename,snippet}] }.
  learnAskLesson: (id, question) => request(`/learn/lessons/${id}/ask`, { method: 'POST', body: JSON.stringify({ question }) }),
  // Submissions to the publisher (design/013): reflections (blockId set) + questions.
  learnSubmit: (lessonId, { blockId = null, kind, body }) =>
    request(`/learn/lessons/${lessonId}/submit`, { method: 'POST', body: JSON.stringify({ blockId, kind, body }) }),
  learnMySubmissions: (lessonId) => request(`/learn/lessons/${lessonId}/submissions`),
  // design/033 — practice reflections + questions (same thread model).
  practiceSubmit: (practiceId, { blockId = null, kind, body }) =>
    request(`/learn/practices/${practiceId}/submissions`, { method: 'POST', body: JSON.stringify({ blockId, kind, body }) }),
  practiceMySubmissions: (practiceId) => request(`/learn/practices/${practiceId}/submissions`),
  // 2026-10-15 — call when the learner opens the page (not on background reloads).
  practiceRepliesSeen: (practiceId) => request(`/learn/practices/${practiceId}/submissions/seen`, { method: 'POST' }),
  lessonRepliesSeen: (lessonId) => request(`/learn/lessons/${lessonId}/submissions/seen`, { method: 'POST' }),
  // Edit one's own submission (the reflection/question body).
  learnEditSubmission: (submissionId, body) =>
    request(`/learn/submissions/${submissionId}`, { method: 'PUT', body: JSON.stringify({ body }) }),
  // Reply into a submission's conversation thread.
  learnReplySubmission: (submissionId, body) =>
    request(`/learn/submissions/${submissionId}/reply`, { method: 'POST', body: JSON.stringify({ body }) }),
  // design/033 — Manager tools → Learner Responses.
  responsesList: ({ status, type, program, q } = {}) => {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (type) p.set('type', type);
    if (program) p.set('program', program);
    if (q) p.set('q', q);
    const qs = p.toString();
    return request(`/responses${qs ? `?${qs}` : ''}`);
  },
  responsesCount: () => request('/responses/count'),
  responseDetail: (id) => request(`/responses/${id}`),
  responseReply: (id, body) => request(`/responses/${id}/reply`, { method: 'POST', body: JSON.stringify({ body }) }),
  responseSettings: () => request('/responses/settings'),
  setResponseSettings: (responseEmail) =>
    request('/responses/settings', { method: 'PUT', body: JSON.stringify({ responseEmail }) }),
  // Publisher: course members + one member's progress/submissions.
  adminCourseMembers: (courseId) => request(`/admin/learn/courses/${courseId}/members`),
  adminCourseMemberDetail: (courseId, personId) => request(`/admin/learn/courses/${courseId}/members/${personId}`),
  // Publisher replies to a learner's reflection/question.
  adminReplySubmission: (submissionId, body) =>
    request(`/admin/learn/submissions/${submissionId}/reply`, { method: 'POST', body: JSON.stringify({ body }) }),

  // Learn — authoring (superadmin/publisher). Courses are top-level (V19).
  // Course cover image (publisher). Served publicly at courseCoverUrl(id).
  uploadCourseCover: (id, file) => uploadFile(`/admin/learn/courses/${id}/cover`, file),
  deleteCourseCover: (id) => request(`/admin/learn/courses/${id}/cover`, { method: 'DELETE' }),
  courseCoverUrl: (id) => `${API_BASE_URL}/learn/courses/${id}/cover`,
  // 2026-10-17 — lesson icon image.
  uploadLessonCover: (id, file) => uploadFile(`/admin/learn/lessons/${id}/cover`, file),
  deleteLessonCover: (id) => request(`/admin/learn/lessons/${id}/cover`, { method: 'DELETE' }),
  lessonCoverUrl: (id) => `${API_BASE_URL}/learn/lessons/${id}/cover`,
  adminListCourses: () => request('/admin/learn/courses'),
  adminCreateCourse: (body) => request('/admin/learn/courses', { method: 'POST', body: JSON.stringify(body) }),
  adminUpdateCourse: (id, body) => request(`/admin/learn/courses/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  adminListInstructors: () => request('/admin/learn/instructors'),
  adminSetCourseInstructor: (id, personId) =>
    request(`/admin/learn/courses/${id}/instructor`, { method: 'PUT', body: JSON.stringify({ personId: personId || null }) }),
  adminDeleteCourse: (id) => request(`/admin/learn/courses/${id}`, { method: 'DELETE' }),
  adminListLessons: (courseId) => request(`/admin/learn/courses/${courseId}/lessons`),
  adminGetLesson: (id) => request(`/admin/learn/lessons/${id}`),
  adminReorderLessons: (courseId, lessonIds) =>
    request(`/admin/learn/courses/${courseId}/lessons/order`, { method: 'PUT', body: JSON.stringify({ lessonIds }) }),
  adminCreateLesson: (courseId, body) => request(`/admin/learn/courses/${courseId}/lessons`, { method: 'POST', body: JSON.stringify(body) }),
  adminUpdateLesson: (id, body) => request(`/admin/learn/lessons/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  adminDeleteLesson: (id) => request(`/admin/learn/lessons/${id}`, { method: 'DELETE' }),
  adminGetBlocks: (lessonId) => request(`/admin/learn/lessons/${lessonId}/blocks`),
  adminSetBlocks: (lessonId, blocks) => request(`/admin/learn/lessons/${lessonId}/blocks`, { method: 'PUT', body: JSON.stringify({ blocks }) }),
  adminUploadLearnMedia: (file) => uploadFile('/admin/learn/media', file),
  // Large media (> MAX_UPLOAD_BYTES): signed PUT straight to GCS, then
  // finalize so gw registers it + enqueues ingest (B4.1b). Bypasses the
  // multipart/Cloud-Run size cap. Returns the same MediaDescriptor shape.
  adminCreateSignedUpload: (filename, contentType) =>
    request('/admin/learn/media/signed-upload', { method: 'POST', body: JSON.stringify({ filename, contentType }) }),
  adminFinalizeUpload: ({ storageUri, contentType, filename, size }) =>
    request('/admin/learn/media/finalize', { method: 'POST', body: JSON.stringify({ storageUri, contentType, filename, size }) }),
  adminUploadLearnMediaLarge: async (file) => {
    const ct = file.type || 'application/octet-stream';
    const { uploadUrl, storageUri, contentType } = await api.adminCreateSignedUpload(file.name, ct);
    // PUT directly to GCS. Content-Type MUST match what gw signed.
    const put = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
    if (!put.ok) {
      const e = new Error(`Direct upload to storage failed (HTTP ${put.status}). `
        + 'If this persists, the storage bucket may need CORS configured.');
      e.status = put.status; throw e;
    }
    return api.adminFinalizeUpload({ storageUri, contentType, filename: file.name, size: file.size });
  },
  // Routes by size: small files go multipart through gw; large files use the
  // signed-URL direct path. One call site for the editor.
  adminUploadLearnMediaSmart: (file) =>
    (file.size > MAX_UPLOAD_BYTES ? api.adminUploadLearnMediaLarge(file) : api.adminUploadLearnMedia(file)),
  adminIngestStatus: (uri) => request(`/admin/learn/ingest?uri=${encodeURIComponent(uri)}`),
  // Ask-AI index (B4.4): build/refresh a lesson's RAG index, then poll status.
  adminIndexLesson: (id) => request(`/admin/learn/lessons/${id}/index`, { method: 'POST' }),
  adminLessonIndexStatus: (id) => request(`/admin/learn/lessons/${id}/index-status`),
  // On-demand transcription (B4.2): kick a Whisper run, then poll status.
  adminTranscribe: (uri) => request(`/admin/learn/transcribe?uri=${encodeURIComponent(uri)}`, { method: 'POST' }),
  adminTranscriptStatus: (uri) => request(`/admin/learn/transcript?uri=${encodeURIComponent(uri)}`),

  // "My" endpoints for the Home dashboard (design/020). Filtered to only what
  // the user is engaged with (enrolled / member / participant). Distinct from
  // the full-catalog listings above which back the sidebar Groups/Events pages.
  myCourses: () => request('/learn/my/courses'),
  myPractices: () => request('/learn/my/practices'),
  myGroups: () => request('/my/groups'),
  myEvents: () => request('/my/events'),

  // Practices (design/019) — Library entity, independent per-Practice pricing,
  // many-to-many with Courses (association is discovery-only, not access).
  // Learner-side (reader):
  listLibraryPractices: () => request('/learn/library/practices'),
  listCoursePractices: (courseId) => request(`/learn/courses/${courseId}/practices`),
  listHomePractices: () => request('/learn/home/practices'),
  getLearnPractice: (id) => request(`/learn/practices/${id}`),
  practiceCoverUrl: (id) => `${API_BASE_URL}/learn/practices/${id}/cover`,
  learnPracticeCheckout: (id) => request(`/learn/practices/${id}/checkout`, { method: 'POST' }),
  enrollLearnPractice: (id) => request(`/learn/practices/${id}/enroll`, { method: 'POST' }),
  listPracticeNotes: (id) => request(`/learn/practices/${id}/notes`),
  createPracticeNote: (id, body) =>
    request(`/learn/practices/${id}/notes`, { method: 'POST', body: JSON.stringify({ body }) }),
  updatePracticeNote: (noteId, body) =>
    request(`/learn/practice-notes/${noteId}`, { method: 'PUT', body: JSON.stringify({ body }) }),
  deletePracticeNote: (noteId) =>
    request(`/learn/practice-notes/${noteId}`, { method: 'DELETE' }),
  // Publisher/authoring (superadmin/publisher):
  adminListPractices: () => request('/admin/learn/practices'),
  adminGetPractice: (id) => request(`/admin/learn/practices/${id}`),
  adminCreatePractice: (body) => request('/admin/learn/practices', { method: 'POST', body: JSON.stringify(body) }),
  adminUpdatePractice: (id, body) => request(`/admin/learn/practices/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  adminDeletePractice: (id, force = false) => request(`/admin/learn/practices/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' }),
  adminPublishPractice: (id) => request(`/admin/learn/practices/${id}/publish`, { method: 'POST' }),
  adminUnpublishPractice: (id) => request(`/admin/learn/practices/${id}/unpublish`, { method: 'POST' }),
  adminGetPracticeBlocks: (id) => request(`/admin/learn/practices/${id}/blocks`),
  adminSetPracticeBlocks: (id, blocks) =>
    request(`/admin/learn/practices/${id}/blocks`, { method: 'PUT', body: JSON.stringify({ blocks }) }),
  adminUploadPracticeCover: (id, file) => uploadFile(`/admin/learn/practices/${id}/cover`, file),
  adminDeletePracticeCover: (id) => request(`/admin/learn/practices/${id}/cover`, { method: 'DELETE' }),
  adminGetPracticeCourses: (id) => request(`/admin/learn/practices/${id}/courses`),
  adminAssociateCourse: (id, courseId) =>
    request(`/admin/learn/practices/${id}/courses/${courseId}`, { method: 'POST' }),
  adminDisassociateCourse: (id, courseId) =>
    request(`/admin/learn/practices/${id}/courses/${courseId}`, { method: 'DELETE' }),
  adminSearchCourses: (q) =>
    request(`/admin/learn/courses/search${q ? '?q=' + encodeURIComponent(q) : ''}`),
  adminPracticeMembers: (practiceId) => request(`/admin/learn/practices/${practiceId}/members`),

  // Practice invites (design/022). Publisher-side + invitee-side.
  adminListPracticeInvites: (practiceId) => request(`/admin/learn/practices/${practiceId}/invites`),
  adminInviteToPractice: (practiceId, { email, name, freeAccess }) =>
    request(`/admin/learn/practices/${practiceId}/invites`, { method: 'POST', body: JSON.stringify({ email, name, freeAccess: !!freeAccess }) }),
  adminResendPracticeInvite: (practiceId, inviteId) =>
    request(`/admin/learn/practices/${practiceId}/invites/${inviteId}/resend`, { method: 'POST' }),
  adminRevokePracticeInvite: (practiceId, inviteId) =>
    request(`/admin/learn/practices/${practiceId}/invites/${inviteId}`, { method: 'DELETE' }),
  listMyPracticeInvites: () => request('/learn/practice-invites/me'),
  acceptPracticeInvite: (tokenOrCode) =>
    request(`/learn/practice-invites/${encodeURIComponent(tokenOrCode)}/accept`, { method: 'POST' }),
  declinePracticeInvite: (tokenOrCode) =>
    request(`/learn/practice-invites/${encodeURIComponent(tokenOrCode)}/decline`, { method: 'POST' }),

  // Event invites (design/022). Owner/superadmin manages; accept adds
  // the invitee to event_participants.
  listEventInvites: (eventId) => request(`/events/${eventId}/invites`),
  inviteToEvent: (eventId, { email, name, freeAccess }) =>
    request(`/events/${eventId}/invites`, { method: 'POST', body: JSON.stringify({ email, name, freeAccess: !!freeAccess }) }),
  resendEventInvite: (eventId, inviteId) =>
    request(`/events/${eventId}/invites/${inviteId}/resend`, { method: 'POST' }),
  revokeEventInvite: (eventId, inviteId) =>
    request(`/events/${eventId}/invites/${inviteId}`, { method: 'DELETE' }),
  listMyEventInvites: () => request('/event-invites/me'),
  acceptEventInvite: (tokenOrCode) =>
    request(`/event-invites/${encodeURIComponent(tokenOrCode)}/accept`, { method: 'POST' }),
  declineEventInvite: (tokenOrCode) =>
    request(`/event-invites/${encodeURIComponent(tokenOrCode)}/decline`, { method: 'POST' }),

  // Admin / superadmin (gw gates on Person.is_admin)
  listAdminUsers: ({ q, limit, cursor } = {}) => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (limit) p.set('limit', String(limit));
    if (cursor) p.set('cursor', cursor);
    const qs = p.toString();
    return request(`/admin/users${qs ? '?' + qs : ''}`);
  },
  markPasswordChanged: () => request('/me/password-changed', { method: 'POST' }),
  getAdminUser: (id) => request(`/admin/users/${id}`),
  // design/036 — suspend (with a reason the person sees) / unsuspend.
  adminSuspendUser: (id, reason) =>
    request(`/admin/users/${id}/suspend`, { method: 'POST', body: JSON.stringify({ reason: reason || null }) }),
  adminUnsuspendUser: (id) => request(`/admin/users/${id}/unsuspend`, { method: 'POST' }),
  adminDeleteUser: (id, { blacklist = false } = {}) =>
    request(`/admin/users/${id}${blacklist ? '?blacklist=true' : ''}`, { method: 'DELETE' }),
  adminSetPublisher: (id, publisher) =>
    request(`/admin/users/${id}/publisher`, { method: 'PUT', body: JSON.stringify({ publisher }) }),
  // design/021: Event Leader = can create + manage own events.
  adminSetEventLeader: (id, eventLeader) =>
    request(`/admin/users/${id}/event-leader`, { method: 'PUT', body: JSON.stringify({ eventLeader }) }),
  adminReindexAll: () => request('/admin/maintenance/reindex-all', { method: 'POST' }),
  adminReindexStatus: () => request('/admin/maintenance/reindex-status'),
  /** One-shot dashboard aggregator (design/021 UX follow-up) — stats +
   *  attention items + financial + health lights, all in one call. */
  adminDashboard: () => request('/admin/dashboard'),

  // Cross-entity associations (design/023). Kinds: course|practice|group|event.
  associate:    (leftKind, leftId, rightKind, rightId) =>
    request(`/admin/associations/${leftKind}/${leftId}/${rightKind}/${rightId}`, { method: 'POST' }),
  disassociate: (leftKind, leftId, rightKind, rightId) =>
    request(`/admin/associations/${leftKind}/${leftId}/${rightKind}/${rightId}`, { method: 'DELETE' }),
  listAssociated: (leftKind, leftId, rightKind) =>
    request(`/admin/associations/${leftKind}/${leftId}/${rightKind}`),
  searchAssociations: (kind, q) =>
    request(`/admin/associations/search?kind=${encodeURIComponent(kind)}${q ? '&q=' + encodeURIComponent(q) : ''}`),
  relatedAssociations: (leftKind, leftId) =>
    request(`/admin/associations/${leftKind}/${leftId}/related`),
  /** Public read-only related lookup for the detail-page "Related …" strips. */
  related: (kind, id) => request(`/related/${kind}/${id}`),

  // Group / Event Stripe checkout (V37).
  groupCheckout: (id) => request(`/groups/${id}/checkout`, { method: 'POST' }),
  eventCheckout: (id) => request(`/events/${id}/checkout`, { method: 'POST' }),
  // Registration wizard (design/024) — empty body; server reads current
  // Person and returns {mode:"joined"|"checkout", ...}. 400 if profile
  // (first/last/email/phone) is incomplete.
  registerForEvent: (id) => request(`/events/${id}/register`, { method: 'POST' }),

  // Sidebar NEW-badge state (design/024, V42): per-user last-visited
  // timestamps persisted server-side so the badge clears cross-device.
  getSeen: () => request('/me/seen'),
  markSeen: (category) => request(`/me/seen/${category}`, { method: 'POST' }),
  // Tiny "does sidebar need a NEW pill?" endpoint — 4 booleans, computed
  // server-side from MAX(created_at) vs person_seen (design/024).
  newFlags: () => request('/me/new-flags'),

  // Notifications (design/016)
  notificationsFeed: () => request('/notifications'),
  notificationsUnreadCount: () => request('/notifications/unread-count'),
  // $4.99/month or $49.99/year subscription (design/025, 028). Web purchases go through Stripe.
  billingStatus: () => request('/billing/status'),
  // design/027 — attach this (unregistered) login to an existing account.
  // design/030 — owners remove members (optional Stripe refund).
  memberPayments: (kind, itemId) => request(`/members/${kind}/${itemId}/payments`),
  memberCounts: (kind) => request(`/member-counts/${kind}`),
  memberList: (kind, itemId) => request(`/members/${kind}/${itemId}`),
  removeMember: (kind, itemId, personId, body) =>
    request(`/members/${kind}/${itemId}/${personId}/remove`, { method: 'POST', body: JSON.stringify(body || {}) }),
  // design/029 — superadmin moderation.
  modReports: (status = 'open') => request(`/admin/moderation/reports?status=${status}`),
  modOpenCount: () => request('/admin/moderation/reports/open-count'),
  modDismiss: (postId) => request(`/admin/moderation/posts/${postId}/dismiss`, { method: 'POST' }),
  modHide: (postId) => request(`/admin/moderation/posts/${postId}/hide`, { method: 'POST' }),
  modRemoveUser: (postId) => request(`/admin/moderation/posts/${postId}/remove-user`, { method: 'POST' }),
  modTerms: () => request('/admin/moderation/terms'),
  modAddTerm: (term) => request('/admin/moderation/terms', { method: 'POST', body: JSON.stringify({ term }) }),
  modDeleteTerm: (id) => request(`/admin/moderation/terms/${id}`, { method: 'DELETE' }),
  // Sign-up: is this username free? Works before the login exists.
  // design/035 — "Email members when published" for events / groups.
  announceState: (kind, id) => request(`/announce/${kind}/${id}`),
  setAnnounce: (kind, id, announce) =>
    request(`/announce/${kind}/${id}`, { method: 'PUT', body: JSON.stringify({ announce }) }),
  // design/035 — unsubscribe link from new course / practice emails (no sign-in).
  unsubscribeCheck: (t) => request(`/public/unsubscribe?t=${encodeURIComponent(t)}`),
  unsubscribe: (t) => request(`/public/unsubscribe?t=${encodeURIComponent(t)}`, { method: 'POST' }),
  usernameAvailable: (username) =>
    request(`/public/username-available?username=${encodeURIComponent(username)}`),
  linkAccount: (idToken) => request('/me/link', { method: 'POST', body: JSON.stringify({ idToken }) }),
  // design/027 — superadmin creates a content publisher / event leader.
  adminSetGroupLeader: (id, groupLeader) =>
    request(`/admin/users/${id}/group-leader`, { method: 'PUT', body: JSON.stringify({ groupLeader }) }),
  // design/032 — Leaders tab (Publishers + superadmin).
  listLeaders: (q) => request(`/leaders${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  inviteLeader: (body) => request('/leaders', { method: 'POST', body: JSON.stringify(body) }),
  setLeaderRoles: (id, roles) => request(`/leaders/${id}`, { method: 'PUT', body: JSON.stringify(roles) }),
  resendLeaderInvite: (id) => request(`/leaders/${id}/resend`, { method: 'POST' }),
  adminCreateStaff: (body) => request('/admin/staff', { method: 'POST', body: JSON.stringify(body) }),
  // design/028 — plan: 'monthly' | 'yearly'.
  startSubscriptionCheckout: (plan = 'monthly') =>
    request('/billing/checkout', { method: 'POST', body: JSON.stringify({ plan }) }),
  openBillingPortal: () => request('/billing/portal', { method: 'POST' }),
  // Back from Stripe: apply the subscription now (don't wait for the webhook).
  // One-time purchases (course / practice / group / event) — grant on return from Stripe.
  confirmPurchase: (sessionId) =>
    request('/purchases/confirm', { method: 'POST', body: JSON.stringify({ sessionId }) }),

  confirmCheckout: (sessionId) =>
    request('/billing/checkout/confirm', { method: 'POST', body: JSON.stringify({ sessionId }) }),

  // Bell badge: unread notifications + pending invites, one request.
  notificationsBadge: () => request('/notifications/badge'),
  // V62 — opened Notifications / Invites: current pending invites stop counting in the bell.
  notificationsInvitesSeen: () => request('/notifications/invites-seen', { method: 'POST' }),
  // Event / Group conversations with new posts (the "boards" part of the badge).
  notificationsBoards: () => request('/notifications/boards'),
  // design/033 — instructor replies to my reflections / questions I haven't opened.
  notificationsReplies: () => request('/notifications/replies'),

  // Community boards (design/026). kind = 'event' | 'group'.
  getBoard: (kind, id) => request(`/boards/${kind}/${id}`),
  // Leader: one image / PDF for a post being written → { id, filename, contentType, image, url }.
  uploadBoardAttachment: (kind, id, file) => uploadFile(`/boards/${kind}/${id}/attachments`, file),
  createBoardPost: (kind, id, body) =>
    request(`/boards/${kind}/${id}/posts`, { method: 'POST', body: JSON.stringify(body) }),
  editBoardPost: (kind, id, postId, body) =>
    request(`/boards/${kind}/${id}/posts/${postId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteBoardPost: (kind, id, postId) =>
    request(`/boards/${kind}/${id}/posts/${postId}`, { method: 'DELETE' }),
  restoreBoardPost: (kind, id, postId) =>
    request(`/boards/${kind}/${id}/posts/${postId}/restore`, { method: 'POST' }),
  pinBoardPost: (kind, id, postId, pinned) =>
    request(`/boards/${kind}/${id}/posts/${postId}/${pinned ? 'pin' : 'unpin'}`, { method: 'POST' }),
  reactBoardPost: (kind, id, postId, emoji) =>
    request(`/boards/${kind}/${id}/posts/${postId}/reactions`, { method: 'POST', body: JSON.stringify({ emoji }) }),
  reportBoardPost: (kind, id, postId, reason, note) =>
    request(`/boards/${kind}/${id}/posts/${postId}/report`, { method: 'POST', body: JSON.stringify({ reason, note }) }),
  updateBoardSettings: (kind, id, body) =>
    request(`/boards/${kind}/${id}/settings`, { method: 'PUT', body: JSON.stringify(body) }),
  markBoardRead: (kind, id) => request(`/boards/${kind}/${id}/read`, { method: 'POST' }),
  setBoardNotify: (kind, id, notify) =>
    request(`/boards/${kind}/${id}/notify`, { method: 'PUT', body: JSON.stringify({ notify }) }),
  postBoardRecap: (kind, id, body) =>
    request(`/boards/${kind}/${id}/recap`, { method: 'POST', body: JSON.stringify({ body }) }),
  acceptCommunityGuidelines: () => request('/me/guidelines/accept', { method: 'POST' }),
  listBlocks: () => request('/me/blocks'),
  blockPerson: (personId) => request(`/me/blocks/${personId}`, { method: 'POST' }),
  unblockPerson: (personId) => request(`/me/blocks/${personId}`, { method: 'DELETE' }),
  notificationMarkRead: (id) => request(`/notifications/${id}/read`, { method: 'POST' }),
  notificationsMarkAllRead: () => request('/notifications/read-all', { method: 'POST' }),
  notificationDismiss: (id) => request(`/notifications/${id}/dismiss`, { method: 'POST' }),
  notificationCreate: (body) => request('/notifications', { method: 'POST', body: JSON.stringify(body) }),
  notificationsForScope: (scopeType, scopeId) =>
    request(`/notifications/scope?scopeType=${scopeType}&scopeId=${scopeId}`),
  notificationEdit: (id, body) => request(`/notifications/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  notificationDelete: (id) => request(`/notifications/${id}`, { method: 'DELETE' }),
  listAdminGroups: ({ q } = {}) => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    const qs = p.toString();
    return request(`/admin/groups${qs ? '?' + qs : ''}`);
  },
  listAdminEvents: ({ q, limit } = {}) => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (limit) p.set('limit', String(limit));
    const qs = p.toString();
    return request(`/admin/events${qs ? '?' + qs : ''}`);
  },
  listOrphanGroups: () => request('/admin/orphan-groups'),

  // Icon URL builder (public endpoint; see SecurityConfig.publicIconFilterChain)
  iconUrl: (eventIconUrl) => (eventIconUrl ? `${API_BASE_URL}${eventIconUrl}` : null),

  // Escape hatch for endpoints not yet wrapped (e.g. admin/* in W5).
  request,
};

export default api;
